import { getErrorString } from "../../common/utilities/errorDetails";
import React, { ReactElement, useMemo, useState } from "react";
import {
  ThemeProvider,
  Button,
  TextField,
  InputAdornment,
  IconButton,
} from "@material-ui/core";
import theme from "../../../themes";
import { useFormik } from "formik";
import * as yup from "yup";
import { makeStyles } from "@material-ui/core/styles";
import CircularProgress from "@material-ui/core/CircularProgress";
import EyeOnIcon from "@material-ui/icons/VisibilityOutlined";
import EyeOffIcon from "@material-ui/icons/VisibilityOffOutlined";
import { signInHandler } from "./SignInHandler";
import {
  Link,
  Redirect,
  useHistory,
  useLocation,
} from "react-router-dom";
import { RedirectUri, useEnumStrings } from "../../enumStrings";
import { useGetChamberForMemberLoginQuery } from "../../../graphql/types";
import OneChamberSystemLogo from "../../../images/osc_logo.png";

interface LoginInput {
  email: string;
  password: string;
}

const useStyles = makeStyles((theme) => ({
  pageContainer: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FFFFFF",
    padding: "40px 16px",
    [theme.breakpoints.down("xs")]: {
      padding: "16px 10px",
    },
  },
  formFields: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxWidth: "760px",
    background: "transparent",
    padding: 0,
    boxShadow: "none",
    textAlign: "center",
  },
  chamberLogo: {
    display: "block",
    margin: "0 auto",
    maxWidth: "220px",
    maxHeight: "170px",
    objectFit: "contain",
    [theme.breakpoints.down("xs")]: {
      maxWidth: "170px",
      maxHeight: "130px",
    },
  },
  welcomeTitle: {
    fontFamily: "DM Sans, sans-serif",
    fontWeight: "bold",
    color: theme.palette.text.primary,
    fontSize: "26px",
    lineHeight: 1.4,
    margin: "24px 0 0 0",
    [theme.breakpoints.down("xs")]: {
      fontSize: "22px",
    },
  },
  description: {
    fontFamily: "DM Sans, sans-serif",
    color: "#4A5568",
    fontSize: "17px",
    lineHeight: 1.65,
    margin: "20px 0 0 0",
    [theme.breakpoints.down("xs")]: {
      fontSize: "16px",
    },
  },
  fieldLabel: {
    fontFamily: "DM Sans, sans-serif",
    textAlign: "left",
    fontWeight: "bold",
    fontSize: "18px",
    color: theme.palette.text.primary,
    marginBottom: "2px",
  },
  fieldGroup: {
    marginTop: "28px",
  },
  textField: {
    backgroundColor: "white !important",
  },
  invalidError: {
    textAlign: "center",
    color: "red",
    marginTop: "20px",
  },
  forgotPasswordContainer: {
    textAlign: "right",
    marginTop: "14px",
  },
  forgotPasswordLink: {
    fontFamily: "DM Sans, sans-serif",
    color: "#E1A731",
    textDecoration: "underline",
    fontWeight: "bold",
    fontSize: "18px",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: "24px",
  },
  loginButton: {
    height: "62px",
    width: "340px",
    maxWidth: "100%",
    fontSize: "22px !important",
    textTransform: "none",
  },
  noCredentialsText: {
    fontFamily: "DM Sans, sans-serif",
    fontWeight: "bold",
    fontSize: "15px",
    color: theme.palette.text.primary,
    lineHeight: 1.5,
    margin: "18px auto 0 auto",
    maxWidth: "420px",
  },
  poweredByContainer: {
    marginTop: "48px",
    [theme.breakpoints.down("xs")]: {
      marginTop: "36px",
    },
  },
  poweredByText: {
    fontFamily: "DM Sans, sans-serif",
    color: "#9E9E9E",
    fontSize: "15px",
  },
  poweredByLogos: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "24px",
    marginTop: "14px",
  },
  poweredByLogo: {
    height: "42px",
    width: "auto",
    objectFit: "contain",
    [theme.breakpoints.down("xs")]: {
      height: "34px",
    },
  },
  loaderContainer: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const MemberLogin = (): ReactElement => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();
  const history = useHistory();
  const location = useLocation();
  const [invalidError, setInvalidError] = useState(false);
  const [customError, setCustomError] = useState("");
  const [loader, setLoader] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const chamberId = useMemo(
    () => new URLSearchParams(location.search).get("chamberId")?.trim() || "",
    [location.search]
  );

  const {
    data,
    loading: chamberLoading,
    error: chamberError,
  } = useGetChamberForMemberLoginQuery({
    variables: { chamberId },
    skip: !chamberId,
  });

  const initialValues: LoginInput = {
    email: "",
    password: "",
  };

  const validationSchema = yup.object({
    email: yup.string().required(enumStrings.loginValidation.emailRequired),
    password: yup
      .string()
      .required(enumStrings.loginValidation.passwordRequired),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (input: LoginInput) => {
      setLoader(true);
      try {
        await signInHandler(
          input.email.trim().toLowerCase(),
          input.password.trim(),
          () => history.push(RedirectUri.newPassword)
        );
        setButtonDisabled(true);
      } catch (error) {
        setInvalidError(true);
        if (getErrorString(error, "code") === "NetworkError") {
          setCustomError(enumStrings.loginErrorMessages.networkError);
        } else if (getErrorString(error, "code") === "NotAuthorizedException") {
          setCustomError(enumStrings.loginErrorMessages.invalidCredentials);
        } else if (getErrorString(error, "code") === "UserNotFoundException") {
          setCustomError(enumStrings.loginErrorMessages.invalidCredentials);
        } else if (getErrorString(error, "code") === "CapUserTryingToSignIn") {
          setCustomError(enumStrings.loginErrorMessages.accessDenied);
        } else if (getErrorString(error, "code") === "UserDisabled") {
          setCustomError(enumStrings.loginErrorMessages.invalidCredentials);
        }
        setLoader(false);
        setButtonDisabled(false);
      }
    },
  });

  // no chamber id in the url or the chamber lookup failed... so fall back to the generic login page
  if (!chamberId || chamberError || (!chamberLoading && !data?.getChamber)) {
    return <Redirect to={RedirectUri.login} />;
  }

  if (chamberLoading) {
    return (
      <ThemeProvider theme={theme}>
        <div className={classes.loaderContainer}>
          <CircularProgress color="primary" />
        </div>
      </ThemeProvider>
    );
  }

  const chamber = data?.getChamber;

  return (
    <ThemeProvider theme={theme}>
      <div className={classes.pageContainer}>
        <form className={classes.formFields} onSubmit={formik.handleSubmit}>
          {chamber?.logo ? (
            <img
              src={chamber.logo}
              className={classes.chamberLogo}
              alt={chamber.name}
            />
          ) : null}
          <h1 className={classes.welcomeTitle}>
            {enumStrings.memberLoginText.welcomeTitleLine1}
            <br />
            {enumStrings.memberLoginText.welcomeTitleLine2.replace(
              "{{chamberName}}",
              chamber?.name || ""
            )}
          </h1>
          <p className={classes.description}>
            {enumStrings.memberLoginText.description}
          </p>
          {invalidError ? (
            <div className={classes.invalidError}>{customError}</div>
          ) : null}
          <div className={classes.fieldGroup}>
            <div className={classes.fieldLabel}>
              {enumStrings.memberLoginText.emailLabel}
            </div>
            <TextField
              variant="outlined"
              InputProps={{
                className: classes.textField,
              }}
              fullWidth
              id="email"
              name="email"
              placeholder={enumStrings.memberLoginText.emailPlaceholder}
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
          </div>
          <div className={classes.fieldGroup}>
            <div className={classes.fieldLabel}>
              {enumStrings.memberLoginText.passwordLabel}
            </div>
            <TextField
              variant="outlined"
              InputProps={{
                className: classes.textField,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <EyeOffIcon /> : <EyeOnIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              fullWidth
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder={enumStrings.memberLoginText.passwordPlaceholder}
              value={formik.values.password}
              onChange={formik.handleChange}
              error={
                formik.touched.password && Boolean(formik.errors.password)
              }
              helperText={formik.touched.password && formik.errors.password}
            />
          </div>
          <div className={classes.forgotPasswordContainer}>
            <Link
              className={classes.forgotPasswordLink}
              to={RedirectUri.forgotPassword}
            >
              {enumStrings.memberLoginText.forgotPasswordText}
            </Link>
          </div>
          <div className={classes.buttonContainer}>
            <Button
              type="submit"
              size="large"
              color="primary"
              variant="contained"
              className={classes.loginButton}
              disabled={buttonDisabled || loader}
            >
              {loader ? (
                <CircularProgress />
              ) : (
                enumStrings.memberLoginText.loginButton
              )}
            </Button>
          </div>
          <p className={classes.noCredentialsText}>
            {enumStrings.memberLoginText.noCredentialsText.replace(
              "{{chamberName}}",
              chamber?.name || ""
            )}
          </p>
          <div className={classes.poweredByContainer}>
            <div className={classes.poweredByText}>
              {enumStrings.memberLoginText.poweredByText}
            </div>
            <div className={classes.poweredByLogos}>
              <img
                src={OneChamberSystemLogo}
                className={classes.poweredByLogo}
                alt="One Chamber System"
              />
            </div>
          </div>
        </form>
      </div>
    </ThemeProvider>
  );
};

export default MemberLogin;
